import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dataSourceFlags } from '@/app/dataSourceFlags';
import { devBypassAuthUser } from '@/app/devMode';
import { showToast } from '@/shared/ui/Toast/showToast';
import { handleCaughtApiError } from '@/utils/basic.utils';
import { userTransport, type UpdateUserProfileInput } from './user-transport';

export const userKeys = {
  all: ['user'] as const,
  profile: (userId: string) => [...userKeys.all, 'profile', userId] as const,
  sessionLeader: (meetingId: string) => [...userKeys.all, 'sessionLeader', meetingId] as const,
  internal: (authId: string, email: string) => [...userKeys.all, 'internal', authId, email] as const
};

/** Fetch user profile data by user ID. */
export function useUserProfile(userId: string) {
  return useQuery({
    queryKey: userKeys.profile(userId),
    queryFn: () => userTransport.fetchProfile(userId),
    enabled: !!userId
  });
}

/** Fetch session leader profile data by meeting ID. */
export function useSessionLeader(meetingId: string) {
  return useQuery({
    queryKey: userKeys.sessionLeader(meetingId),
    queryFn: () => userTransport.fetchSessionLeader(meetingId),
    enabled: !!meetingId
  });
}

/** Mutation hook to update user profile data. */
export function useUpdateUserProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (profile: UpdateUserProfileInput) => userTransport.updateProfile(profile),
    onSuccess: (data, variables) => {
      // Invalidate both the specific profile and potentially the internal user query
      if (!dataSourceFlags.useLiveUsers) {
        queryClient.setQueryData(userKeys.profile(variables.userId), data);
        queryClient.setQueryData(userKeys.internal(devBypassAuthUser.sub, devBypassAuthUser.email), data);
      }
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      showToast.success('Successfully updated profile');
    },
    onError: (error) => {
      handleCaughtApiError(error, 'Error updating profile');
    }
  });
}

/** Mutation hook to upload a new avatar for the user. */
export function useUploadUserAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, file }: { userId: string; file: Blob }) => userTransport.uploadAvatar(userId, file),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(userKeys.profile(variables.userId), data);
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      showToast.success('Profile photo updated');
    },
    onError: (error) => {
      handleCaughtApiError(error, 'Could not update profile photo');
    }
  });
}

/** Mutation hook to remove the user's avatar. */
export function useDeleteUserAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) => userTransport.deleteAvatar(userId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(userKeys.profile(variables.userId), data);
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      showToast.success('Profile photo removed');
    },
    onError: (error) => {
      handleCaughtApiError(error, 'Could not remove profile photo');
    }
  });
}

/** Fetch internal user object using Auth0 ID and email. */
export function useInternalUser(authId: string, email: string) {
  return useQuery({
    queryKey: userKeys.internal(authId, email),
    queryFn: () => userTransport.fetchInternalUser(authId, email),
    enabled: !dataSourceFlags.useLiveUsers || (!!authId && !!email)
  });
}
